import {
  CanvasTexture,
  GLSL3,
  Mesh,
  PlaneGeometry,
  Ray,
  SRGBColorSpace,
  ShaderMaterial,
  Vector3,
} from 'three';
import type { PeerPose } from './peers';
import type { AnnotationLayer } from '../render/annotations';
import vertexShader from '../render/shaders/playerSelection.vert?raw';
import fragmentShader from '../render/shaders/visitorName.frag?raw';

export interface VisitorHit {
  readonly peer: PeerPose;
  readonly distance: number;
}

export function pickVisitor(ray: Ray, peers: readonly PeerPose[]): VisitorHit | null {
  let found: VisitorHit | null = null;
  const point = new Vector3();
  for (const peer of peers) {
    const ground = peer.y - (peer.mode === 'boat' ? 1.45 : 1.7);
    const bottom = new Vector3(peer.x, ground + 0.25, peer.z);
    const top = new Vector3(peer.x, ground + 1.5, peer.z);
    if (ray.distanceSqToSegment(bottom, top, point) > 0.35 ** 2) continue;
    const distance = ray.origin.distanceTo(point);
    if (distance > 40 || (found && distance >= found.distance)) continue;
    found = { peer, distance };
  }
  return found;
}

function selectionTexture(): CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 256;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Player selection requires a canvas context');
  for (const [colour, width] of [
    ['#25190e', 16],
    ['#c3a464', 11],
    ['#ead8b0', 4],
  ] as const) {
    context.strokeStyle = colour;
    context.lineWidth = width;
    context.lineCap = 'round';
    for (let angle = 0; angle < 4; angle++) {
      context.save();
      context.translate(128, 128);
      context.rotate(Math.PI / 4 + (angle * Math.PI) / 2);
      context.beginPath();
      context.moveTo(34, 1);
      context.lineTo(59, -2);
      context.lineTo(84, 1);
      context.stroke();
      context.restore();
    }
  }
  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  return texture;
}

export function createPlayerSelection(layer: AnnotationLayer) {
  const texture = selectionTexture();
  const geometry = new PlaneGeometry(1.8, 1.8);
  const material = new ShaderMaterial({
    glslVersion: GLSL3,
    vertexShader,
    fragmentShader,
    uniforms: {
      tName: { value: texture },
      tGeom: layer.depth,
      uResolution: layer.resolution,
    },
    transparent: true,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  });
  const mesh = new Mesh(geometry, material);
  mesh.name = 'selected-visitor';
  mesh.rotation.x = -Math.PI / 2;
  mesh.visible = false;
  layer.scene.add(mesh);
  return {
    update(peer: PeerPose | null): void {
      mesh.visible = !!peer;
      if (peer)
        mesh.position.set(peer.x, peer.y - (peer.mode === 'boat' ? 1.45 : 1.7) + 0.06, peer.z);
    },
    dispose(): void {
      layer.scene.remove(mesh);
      geometry.dispose();
      material.dispose();
      texture.dispose();
    },
  };
}
